
import './style.scss';
import { Add, Delete, Edit, FilterAlt, Refresh } from "@mui/icons-material";
import { Grid, IconButton } from "@mui/material";
import DataGrid from '../../../../components/DataGrid';
import ContentTitle from "../../../../components/Content/ContentTitle";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import IAccessProfile from "../../../../interfaces/IAccessProfile";
import { useEffect, useState, useCallback } from "react";
import AccessProfileService from "../../../../services/AccessProfileService";
import FormHelper from "../../../../helpers/FormHelper";
import RegistrationStatus from "../../../../components/RegistrationStatus";
import { useUserContext } from "../../../../contexts/UserContext";
import RemoveDialog from "./RemoveDialog";
import DefaultConstants from '../../../../data/Constants';
import AddEditDialog from "./AddEditDialog";
import { INavigationLink } from '../../../../interfaces/INavigation';
import Filters from './Filters';
import { RegistrationStatusEnum } from '../../../../data/Enums';
import Tooltip from '../../../../components/Tooltip';
import useDrawer from '../../../../hooks/useDrawer';
import useDialog from '../../../../hooks/useDialog';


const AccessProfile = (linkProps: INavigationLink) => {
    const { translate } = useLanguageContext();
    const { user, userPermissions } = useUserContext();

    const filtersDrawer = useDrawer();
    const [filters, setFilters] = useState<FormData | undefined>(undefined);

    const [data, setData] = useState<IAccessProfile[]>([]);
    const [selectedRow, setSelectedRow] = useState<IAccessProfile | undefined>(undefined);

    const addEditDialog = useDialog();
    const removeDialog = useDialog();


    useEffect(() => {
        handleRefresh();
        // eslint-disable-next-line
    }, [filters]);
    
    
    const handleRefresh = useCallback(() => {
        const formData = filters ?? FormHelper.Create(undefined);
        
        AccessProfileService.List(formData)
            .then((resp) => {
                setData(resp.data.accessProfiles as IAccessProfile[]);
            });

        // eslint-disable-next-line
    }, [filters]);


    const handleAddEdit = useCallback((row: IAccessProfile | undefined) => {
        setSelectedRow(row);
        addEditDialog.handleOpen();

        // eslint-disable-next-line
    }, []);
    

    const handleRemove = useCallback((row: IAccessProfile) => {
        setSelectedRow(row);
        removeDialog.handleOpen();

        // eslint-disable-next-line
    }, []);


    const dataGridColumns = [
        {
            field: 'description', 
            headerName: translate('description'), 
            flex: 1, 
            minWidth: 200 
        }, {
            field: 'registrationStatusId', 
            type: DefaultConstants.dataGridRegistrationStatusType, 
            headerName: translate('situation'), 
            flex: 1, 
            minWidth: 200, 
            renderCell: (params: any) => {
                return <RegistrationStatus id={params.row.registrationStatusId} />;
            }
        }, {
            field: 'actions', 
            type: 'actions', 
            headerName: translate('actions'), 
            width: 200, 
            renderCell: (params: any) => {
                if (params.row.administrator && !user?.administrator) {
                    return;
                }

                if (Number(params.row.registrationStatusId) === RegistrationStatusEnum.Excluded) {
                    return;
                }

                return (
                    <Grid item>
                        {
                            userPermissions.includes(`${linkProps.alias[0]}.Edit`) && (
                                <Tooltip title={translate('edit')}>
                                    <IconButton className='actionbutton-edit' onClick={() => handleAddEdit(params.row)}>
                                        <Edit fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }

                        {
                            (!params.row.administrator || user?.administrator) && userPermissions.includes(`${linkProps.alias[0]}.Remove`) && (
                                <Tooltip title={translate('remove')}>
                                    <IconButton className='actionbutton-remove' onClick={() => handleRemove(params.row)}>
                                        <Delete fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }
                    </Grid>
                );
            }
        }
    ];

    return (
        <>
            <Grid item xs={12} className='grid-header'>
                <Grid item className='grid-title'>
                    <ContentTitle {...linkProps} />
                </Grid>

                <Grid item className='grid-tools'>
                    {
                        userPermissions.includes(`${linkProps.alias[0]}.Add`) && (
                            <Tooltip title={translate('add')}>
                                <IconButton onClick={() => handleAddEdit(undefined)}>
                                    <Add />
                                </IconButton>
                            </Tooltip>
                        )
                    }
                    
                    <Tooltip title={translate('filter')}>
                        <IconButton onClick={() => filtersDrawer.handleOpen()}>
                            <FilterAlt />
                        </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={translate('refresh')}>
                        <IconButton onClick={handleRefresh}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            <Grid container className='grid-content'>
                <Grid item xs={12} className='paper-content'>
                    <DataGrid 
                        columns={dataGridColumns} 
                        data={data} 
                        openSearch={false}
                        onCellDoubleClick={params => {
                            if (params.row.administrator && !user?.administrator) {
                                return;
                            }

                            if (Number(params.row.registrationStatusId) === RegistrationStatusEnum.Excluded) {
                                return;
                            }

                            if (userPermissions.includes(`${linkProps.alias[0]}.Edit`)) {
                                handleAddEdit(params.row);
                                return;
                            }
                            
                            if (userPermissions.includes(`${linkProps.alias[0]}.Remove`)) {
                                handleRemove(params.row);
                                return;
                            }
                        }}
                    />
                </Grid>
            </Grid>

            <Filters
                open={filtersDrawer.open}
                handleClose={() => filtersDrawer.handleClose()}
                handleApplyFilters={(formData) => setFilters(formData)}
            />

            <AddEditDialog
                open={addEditDialog.open}
                handleClose={() => addEditDialog.handleClose()}
                data={selectedRow}
                handleListRefresh={handleRefresh}
            />

            <RemoveDialog
                open={removeDialog.open}
                handleClose={() => removeDialog.handleClose()}
                data={selectedRow}
                handleListRefresh={handleRefresh}
            />
        </>
    );
}

export default AccessProfile;